import React, { useState, useEffect } from 'react';
import { Q3G_WEEKS, Q3G_LINES } from './q3gRates';

// OP (Q3G) plan rate lookup by PPR-line NAME. Q3G_LINES is keyed by PPID; its
// line names match the YOUSA/MEESA line names (trimmed), so we build a
// name -> {rates} map once for the "% to OP Plan" mode.
const Q3G_BY_NAME = {};
Object.values(Q3G_LINES).forEach(l => { Q3G_BY_NAME[l.name.trim()] = l.rates; });
// Latest OP week available (for forecast weeks beyond the plan horizon).
const Q3G_LAST_WEEK = Q3G_WEEKS[Q3G_WEEKS.length - 1];

const PPR_LINES = [
  { id: 'receiveDock', name: 'Receive Dock', type: 'cplh', cartonSource: 'ibCartons' },
  { id: 'eachReceiveSmall', name: '  Each Receive - Small', type: 'uph' },
  { id: 'eachReceiveMedium', name: '  Each Receive - Medium', type: 'uph' },
  { id: 'eachReceiveLarge', name: '  Each Receive - Large', type: 'uph' },
  { id: 'eachReceiveHeavyBulky', name: '  Each Receive - Heavy/Bulky', type: 'uph' },
  { id: 'caseReceive', name: '  Case Receive', type: 'uph' },
  { id: 'lpReceive', name: '  LP Receive', type: 'cplh', cartonSource: '_pidPlusAros' },
  { id: 'palletReceive', name: '  Pallet Receive', type: 'cplh', cartonSource: '_pallets' },
  { id: 'receiveSupport', name: 'Receive Support', type: 'cplh', cartonSource: 'ibCartons' },
  { id: 'caseTransferIn', name: '  Case Transfer In', type: 'uph' },
  { id: 'transferInSupport', name: '  Transfer In Support', type: 'uph' },
  { id: 'cubiscan', name: '  Cubiscan', type: 'uph' },
  { id: 'prepRecorderSmall', name: '  Prep Recorder - Small', type: 'uph' },
  { id: 'prepRecorderMedium', name: '  Prep Recorder - Medium', type: 'uph' },
  { id: 'prepRecorderLarge', name: '  Prep Recorder - Large', type: 'uph' },
  { id: 'prepRecorderHeavyBulky', name: '  Prep Recorder - Heavy/Bulky', type: 'uph' },
  { id: 'prepSupport', name: '  Prep Support', type: 'uph' },
  { id: 'rsrSupport', name: '  RSR Support', type: 'uph' },
  { id: 'ibLeadPa', name: '  IB Lead/PA', type: 'uph' },
  { id: 'ibProblemSolve', name: '  IB Problem Solve', type: 'uph' },
  { id: 'transferOut', name: 'Transfer Out', type: 'cplh', cartonSource: 'daCartons' },
  { id: 'transferOutDock', name: 'Transfer Out Dock', type: 'cplh', cartonSource: 'daCartons' },
  { id: 'toLeadPa', name: '  TO Lead/PA', type: 'uph' },
  { id: 'toProblemSolve', name: '  TO Problem Solve', type: 'uph' },
  { id: 'daBldgTotal', name: 'DA Bldg to Bldg Transfer TOTAL', type: 'cplh', cartonSource: 'daCartons' },
  { id: 'adminHrIt', name: 'Admin/HR/IT', type: 'cplh', cartonSource: 'totalCartons' },
  { id: 'totalInbound', name: 'Total Inbound', type: 'cplh', cartonSource: 'ibCartons' },
  { id: 'transferOutSummary', name: 'Transfer-Out (FC Summary)', type: 'cplh', cartonSource: 'daCartons' },
  { id: 'support', name: 'Support', type: 'cplh', cartonSource: 'totalCartons' },
  { id: 'throughput', name: 'THROUGHPUT', type: 'cplh', cartonSource: 'totalCartons' },
  { id: '_prodThroughput', name: 'Production Only Throughput', type: 'calc' },
];

function getHeatColor(pct) { if (!pct || pct === 0) return '#f5f5f5'; if (pct >= 105) return '#c8e6c9'; if (pct >= 100) return '#e8f5e9'; if (pct >= 95) return '#fff9c4'; if (pct >= 90) return '#ffcc80'; return '#ef9a9a'; }
function getHoursColor(hours) { if (!hours) return '#f5f5f5'; if (hours > 0) return '#c8e6c9'; if (hours < 0) return '#ef9a9a'; return '#f5f5f5'; }

export default function Yousa({ api, site }) {
  const [weeklyData, setWeeklyData] = useState([]);
  const [monthlyData, setMonthlyData] = useState([]);
  const [viewMode, setViewMode] = useState('weekly'); // 'weekly' | 'monthly'
  const [loading, setLoading] = useState(false);
  const getDefaultStart = () => {
    const now = new Date();
    const pacificOffset = -7 * 60;
    const utcMs = now.getTime() + now.getTimezoneOffset() * 60000;
    const pacNow = new Date(utcMs + pacificOffset * 60000);
    const day = pacNow.getDay();
    const sunday = new Date(pacNow);
    sunday.setDate(pacNow.getDate() - day);
    sunday.setDate(sunday.getDate() - 28);
    const y = sunday.getFullYear();
    const m = String(sunday.getMonth() + 1).padStart(2, '0');
    const d = String(sunday.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  };
  const [startWeek, setStartWeek] = useState(getDefaultStart());
  const [endWeek, setEndWeek] = useState('');
  const [rampPct, setRampPct] = useState(0.5);
  const [essentialsOnly, setEssentialsOnly] = useState(true);
  const [toExpanded, setToExpanded] = useState(false); // Transfer Out sub-bucket expander
  const [planMode, setPlanMode] = useState('hwm');

  const hiddenLineIds = ['eachReceiveSmall', 'eachReceiveMedium', 'eachReceiveLarge', 'eachReceiveHeavyBulky', 'caseReceive', 'caseTransferIn', 'transferInSupport', 'cubiscan', 'prepRecorderSmall', 'prepRecorderMedium', 'prepRecorderLarge', 'prepRecorderHeavyBulky', 'prepSupport'];

  useEffect(() => {
    setLoading(true);
    Promise.all([
      fetch(`${api}/api/meesa?site=${site}`).then(r => r.json()),
      fetch(`${api}/api/fclm/cache?site=${site}&date=MEESA_MONTHLY&shift_type=data`).then(r => r.json()).catch(() => []),
    ]).then(([weekly, monthly]) => {
      if (Array.isArray(weekly)) setWeeklyData(weekly); else setWeeklyData([]);
      if (Array.isArray(monthly)) setMonthlyData(monthly); else setMonthlyData([]);
      setLoading(false);
    }).catch(() => { setWeeklyData([]); setMonthlyData([]); setLoading(false); });
  }, [api, site]);

  const thisSunday = (() => {
    const now = new Date();
    const pacNow = new Date(now.getTime() + now.getTimezoneOffset() * 60000 + (-7 * 60) * 60000);
    const s = new Date(pacNow); s.setDate(pacNow.getDate() - pacNow.getDay());
    return `${s.getFullYear()}-${String(s.getMonth() + 1).padStart(2, '0')}-${String(s.getDate()).padStart(2, '0')}`;
  })();
  const filteredData = weeklyData.filter(w => {
    if (w.weekStart < '2025-12-28') return false;
    if (w.weekStart >= thisSunday) return false; // Hide the current in-progress (partial) week
    if (startWeek && w.weekStart < startWeek) return false;
    if (endWeek && w.weekStart > endWeek) return false;
    return true;
  });

  const getWeekRate = (week, line) => {
    if (line.type === 'calc') {
      const c = (week.cartons?.ibCartons || 0) + (week.cartons?.daCartons || 0);
      const h = (week.lines?.totalInbound?.hours || 0) + (week.lines?.daBldgTotal?.hours || 0);
      return h > 0 ? c / h : 0;
    }
    if (line.type === 'cplh' && line.cartonSource) {
      let c = 0;
      if (line.cartonSource === '_pidPlusAros') c = (week.cartons?.pid || 0) + (week.cartons?.aros || 0);
      else if (line.cartonSource === '_pallets') c = (week.cartons?.pr || 0) / 28.2;
      else c = week.cartons?.[line.cartonSource] || 0;
      const h = week.lines?.[line.id]?.hours || 0;
      return h > 0 ? c / h : 0;
    }
    return week.lines?.[line.id]?.rate || 0;
  };

  // HWM week (highest Throughput CPLH this year, excl current). Computed before
  // the forecast so HWM mode can re-base F1-F13 on it.
  const hwmWeek = (() => {
    let best = null, bestRate = 0;
    const weeksToCheck = weeklyData.filter(w => w.weekStart < thisSunday); // historical HWM across all weeks, not just selected timeframe
    weeksToCheck.forEach(w => {
      if (w.weekStart < '2026-01-01') return;
      const c = w.cartons?.totalCartons || 0;
      const h = w.lines?.throughput?.hours || 0;
      const rate = h > 0 ? c / h : 0;
      if (rate > bestRate) { bestRate = rate; best = w; }
    });
    return best;
  })();

  // Forecast week-start dates for F1..F13 (the 13 Sundays from thisSunday on).
  const forecastWeekStarts = [...Array(13)].map((_, i) => {
    const d = new Date(thisSunday + 'T12:00:00');
    d.setDate(d.getDate() + 7 * i);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  });
  // OP (Q3G) plan rate for a line at a given forecast week; falls back to the
  // last OP week if the forecast runs past the plan horizon.
  const opRateForWeek = (line, weekStart) => {
    const rates = Q3G_BY_NAME[line.name.trim()];
    if (!rates) return 0;
    return rates[weekStart] || rates[Q3G_LAST_WEEK] || 0;
  };

  // Top-down forecast, re-based on the selected % to Plan Mode:
  //   best3of4 -> top-3-of-last-4 average, then + WoW ramp (compounding)
  //   hwm      -> HWM week's rate, then + WoW ramp (0.5% over HWM, etc.)
  //   op       -> per-week OP (Q3G) plan rate as-is (an explicit Finance plan;
  //               ramp does NOT apply)
  const forecastByLine = {};
  PPR_LINES.forEach(line => {
    const completeWeeks = filteredData.length > 1 ? filteredData.slice(-4) : [];
    const rates = completeWeeks.map(w => getWeekRate(w, line)).filter(r => r > 0);
    rates.sort((a, b) => b - a);
    const top3 = rates.slice(0, 3);
    const b3of4 = top3.length > 0 ? top3.reduce((s, v) => s + v, 0) / top3.length : 0;
    const hwmRate = hwmWeek ? getWeekRate(hwmWeek, line) : 0;

    let baseline, forecast;
    if (planMode === 'op') {
      baseline = opRateForWeek(line, forecastWeekStarts[0]);
      forecast = forecastWeekStarts.map(ws => {
        const r = opRateForWeek(line, ws);
        return r ? Math.round(r * 10) / 10 : 0;
      });
    } else {
      baseline = planMode === 'hwm' ? hwmRate : b3of4;
      forecast = [...Array(13)].map((_, i) => Math.round(baseline * (1 + (rampPct / 100) * (i + 1)) * 10) / 10);
    }
    forecastByLine[line.id] = { baseline, forecast };
  });

  // Plan target for the historical % to Plan heat-coloring. Uses the SAME
  // baseline the F-columns use, so switching mode moves both together:
  //   op  -> that historical week's OP (Q3G) plan rate
  //   hwm -> the HWM week's rate (flat reference)
  //   b3of4 -> the top-3-of-4 baseline (flat reference)
  const getPlanTarget = (week, line) => {
    if (planMode === 'op') return opRateForWeek(line, week.weekStart) || week.lines?.[line.id]?.planRate || 0;
    return forecastByLine[line.id]?.baseline || 0;
  };

  const cellStyle = { padding: '3px 6px', textAlign: 'center', fontSize: '0.7rem', fontWeight: 500, border: '1px solid #000', whiteSpace: 'nowrap' };
  const headerStyle = { ...cellStyle, fontWeight: 700, background: '#232f3e', color: '#fff' };
  const labelStyle = { ...cellStyle, textAlign: 'left', fontWeight: 600, background: '#fff', position: 'sticky', left: 0, zIndex: 1 };
  const subLabelStyle = { ...cellStyle, textAlign: 'left', fontSize: '0.6rem', color: '#555', paddingLeft: 24 };

  return (
    <div>
      <h2 className="section-title">Tops Down Forecast — {site}</h2>
      <div className="card">
        <div className="form-row" style={{ flexWrap: 'wrap' }}>
          <div className="form-group">
            <button className={`btn ${viewMode === 'weekly' ? 'btn-primary' : 'btn-secondary'}`} onClick={() => setViewMode('weekly')} style={{ fontSize: '0.75rem', padding: '5px 10px' }}>Weekly</button>
            <button className={`btn ${viewMode === 'monthly' ? 'btn-primary' : 'btn-secondary'}`} onClick={() => setViewMode('monthly')} style={{ fontSize: '0.75rem', padding: '5px 10px', marginLeft: 4 }}>Monthly</button>
          </div>
          <div className="form-group"><label>Start</label><input type="date" value={startWeek} onChange={e => setStartWeek(e.target.value)} /></div>
          <div className="form-group"><label>End</label><input type="date" value={endWeek} onChange={e => setEndWeek(e.target.value)} /></div>
          <div className="form-group"><label>WoW Ramp %</label><input type="number" step="0.1" min="0" max="5" value={rampPct} onChange={e => setRampPct(parseFloat(e.target.value) || 0)} disabled={planMode === 'op'} title={planMode === 'op' ? 'Ramp does not apply in OP Plan mode (OP rates are an explicit weekly plan)' : ''} style={{ width: 60, textAlign: 'center', opacity: planMode === 'op' ? 0.4 : 1 }} /></div>
          <div className="form-group">
            <label>% to Plan Mode</label>
            <select value={planMode} onChange={e => setPlanMode(e.target.value)}>
              <option value="op">% to OP Plan</option>
              <option value="best3of4">% to Best 3 of 4</option>
              <option value="hwm">% to HWM</option>
            </select>
          </div>
          <div className="form-group">
            <button className={`btn ${essentialsOnly ? 'btn-primary' : 'btn-secondary'}`} onClick={() => setEssentialsOnly(!essentialsOnly)} style={{ fontSize: '0.8rem', padding: '6px 14px' }}>
              {essentialsOnly ? '📋 Show All' : '🫙 Just the essentials okeeday!'}
            </button>
          </div>
        </div>
        <p style={{ fontSize: '0.8rem', color: '#666' }}>{viewMode === 'weekly' ? `${filteredData.length} weeks` : `${monthlyData.length} months`}</p>
      </div>

      {!loading && viewMode === 'weekly' && filteredData.length > 0 && (
        <div className="card" style={{ overflowX: 'auto' }}>
          <div style={{ marginBottom: 8, textAlign: 'right' }}>
            <button className="btn btn-secondary" style={{ fontSize: '0.75rem', padding: '4px 12px' }} onClick={() => {
              const lines = PPR_LINES.filter(l => !essentialsOnly || !hiddenLineIds.includes(l.id));
              const weeks = filteredData.map(w => w.weekStart);
              const forecastHeaders = [...Array(13)].map((_, i) => `F${i + 1}`);
              let csv = 'Line Item,Type,' + weeks.join(',') + ',' + forecastHeaders.join(',') + '\n';
              lines.forEach(line => {
                const rateLabel = line.type === 'uph' ? 'UPH' : 'CPLH';
                const rates = filteredData.map(w => { const r = getWeekRate(w, line); return r ? r.toFixed(1) : ''; });
                const forecast = forecastByLine[line.id]?.forecast || [];
                csv += `${line.name.trim()},${rateLabel},${rates.join(',')},${forecast.map(f => f ? f.toFixed(1) : '').join(',')}\n`;
                const hours = filteredData.map(w => { const h = line.type === 'calc' ? (w.lines?.totalInbound?.hours || 0) + (w.lines?.daBldgTotal?.hours || 0) : (w.lines?.[line.id]?.hours || 0); return h ? Math.round(h) : ''; });
                csv += `${line.name.trim()},Hours,${hours.join(',')},${forecastHeaders.map(() => '').join(',')}\n`;
              });
              const blob = new Blob([csv], { type: 'text/csv' });
              const a = document.createElement('a'); a.href = URL.createObjectURL(blob); a.download = `yousa_${site}.csv`; a.click();
            }}>📥 Export CSV</button>
          </div>
          <table style={{ borderCollapse: 'collapse', fontSize: '0.7rem', width: '100%' }}>
            <thead>
              <tr>
                <th style={{ ...headerStyle, textAlign: 'left', minWidth: 160, position: 'sticky', left: 0, zIndex: 2 }}>Week</th>
                {filteredData.map((week, i) => <th key={i} style={headerStyle}>{(() => { const d = new Date(week.weekStart + 'T12:00:00'); const jan1 = new Date(d.getFullYear(), 0, 1); return Math.ceil((((d - jan1) / 86400000) + jan1.getDay() + 1) / 7); })()}</th>)}
                {[...Array(13)].map((_, i) => <th key={`f${i}`} style={{ ...headerStyle, background: '#1565c0' }}>F{i + 1}</th>)}
              </tr>
            </thead>
            <tbody>
              {PPR_LINES.filter(line => !essentialsOnly || !hiddenLineIds.includes(line.id)).map(line => {
                const rateLabel = line.type === 'uph' ? 'UPH' : 'CPLH';
                return (
                  <React.Fragment key={line.id}>
                    <tr>
                      <td style={labelStyle}>
                        {line.id === 'transferOut' && (
                          <button onClick={() => setToExpanded(e => !e)}
                            title={toExpanded ? 'Collapse Transfer Out sub-lines' : 'Expand Transfer Out sub-lines'}
                            style={{ marginRight: 6, width: 16, height: 16, lineHeight: '14px', textAlign: 'center', padding: 0, border: '1px solid #999', borderRadius: 3, background: '#eceff1', cursor: 'pointer', fontSize: '0.7rem', fontWeight: 700 }}>
                            {toExpanded ? '−' : '+'}
                          </button>
                        )}
                        {line.name} <span style={{ fontSize: '0.55rem', color: '#888' }}>{rateLabel}</span></td>
                      {filteredData.map((week, i) => {
                        const rate = getWeekRate(week, line);
                        const target = getPlanTarget(week, line);
                        const pct = target > 0 ? (rate / target) * 100 : 0;
                        return <td key={i} style={{ ...cellStyle, background: getHeatColor(pct) }}>{rate ? rate.toFixed(1) : '—'}</td>;
                      })}
                      {(forecastByLine[line.id]?.forecast || []).map((f, i) => (
                        <td key={`f${i}`} style={{ ...cellStyle, background: '#e3f2fd', color: '#1565c0', fontStyle: 'italic' }}>{f ? f.toFixed(1) : '—'}</td>
                      ))}
                    </tr>
                    <tr>
                      <td style={subLabelStyle}>% to Plan</td>
                      {filteredData.map((week, i) => {
                        const rate = getWeekRate(week, line);
                        const target = getPlanTarget(week, line);
                        const pct = target > 0 ? (rate / target) * 100 : 0;
                        return <td key={i} style={{ ...cellStyle, background: getHeatColor(pct), fontSize: '0.6rem' }}>{pct ? pct.toFixed(0) + '%' : '—'}</td>;
                      })}
                      {[...Array(13)].map((_, i) => <td key={`f${i}`} style={{ ...cellStyle, background: '#e3f2fd', color: '#999', fontSize: '0.6rem' }}>—</td>)}
                    </tr>
                    <tr>
                      <td style={subLabelStyle}>Hours</td>
                      {filteredData.map((week, i) => {
                        const h = line.type === 'calc' ? (week.lines?.totalInbound?.hours || 0) + (week.lines?.daBldgTotal?.hours || 0) : (week.lines?.[line.id]?.hours || 0);
                        return <td key={i} style={{ ...cellStyle, fontSize: '0.6rem', color: '#555' }}>{h ? Math.round(h).toLocaleString() : '—'}</td>;
                      })}
                      {[...Array(13)].map((_, i) => <td key={`f${i}`} style={{ ...cellStyle, background: '#e3f2fd', color: '#999', fontSize: '0.6rem' }}>—</td>)}
                    </tr>
                    <tr style={{ borderBottom: '2px solid #000' }}>
                      <td style={subLabelStyle}>Hrs +/-</td>
                      {filteredData.map((week, i) => {
                        const rate = getWeekRate(week, line);
                        const h = line.type === 'calc' ? (week.lines?.totalInbound?.hours || 0) + (week.lines?.daBldgTotal?.hours || 0) : (week.lines?.[line.id]?.hours || 0);
                        const target = getPlanTarget(week, line);
                        let hrsSaved = 0;
                        if (target > 0 && rate > 0 && h > 0) {
                          const cartons = rate * h;
                          hrsSaved = (cartons / target) - h;
                        }
                        return <td key={i} style={{ ...cellStyle, fontSize: '0.6rem', background: getHoursColor(hrsSaved) }}>{hrsSaved ? Math.round(hrsSaved).toLocaleString() : '—'}</td>;
                      })}
                      {[...Array(13)].map((_, i) => <td key={`f${i}`} style={{ ...cellStyle, background: '#e3f2fd', color: '#999', fontSize: '0.6rem' }}>—</td>)}
                    </tr>
                    {/* Expanded Transfer Out sub-lines — derived from each week's
                        transferOut functions (display-only; historical data untouched).
                        Shows Rate (DA cartons / bucket hours) and Hours per bucket. */}
                    {line.id === 'transferOut' && toExpanded && (() => {
                      const TO_BUCKETS = [
                        ['Transfer Out Support', (n) => !['Fluid Load - Case','Wall Builder','Robotics Operator','Palletize - Case'].includes(n)],
                        ['TO Fluid Load', (n) => n === 'Fluid Load - Case' || n === 'Wall Builder'],
                        ['TO RWC', (n) => n === 'Robotics Operator'],
                        ['TO Palletize', (n) => n === 'Palletize - Case'],
                      ];
                      const bHours = (week, pred) => (week.functions?.transferOut || []).reduce((s, f) => s + (pred(f.name) ? (f.hours || 0) : 0), 0);
                      return TO_BUCKETS.map(([bname, pred]) => {
                        if (!filteredData.some(w => bHours(w, pred) > 0)) return null;
                        return (
                          <React.Fragment key={`to-sub-${bname}`}>
                            <tr style={{ background: '#f7faf7' }}>
                              <td style={{ ...subLabelStyle, color: '#1b5e20', fontWeight: 600 }}>↳ {bname} <span style={{ fontSize: '0.55rem', color: '#888' }}>CPLH</span></td>
                              {filteredData.map((week, i) => {
                                const h = bHours(week, pred);
                                const c = week.cartons?.daCartons || 0;
                                const r = h > 0 ? c / h : 0;
                                return <td key={i} style={{ ...cellStyle, background: '#f1f8e9' }}>{r ? r.toFixed(1) : '—'}</td>;
                              })}
                              {[...Array(13)].map((_, i) => <td key={`f${i}`} style={{ ...cellStyle, background: '#e3f2fd', color: '#999' }}>—</td>)}
                            </tr>
                            <tr style={{ background: '#f7faf7' }}>
                              <td style={{ ...subLabelStyle, color: '#1b5e20', paddingLeft: 32 }}>Hours</td>
                              {filteredData.map((week, i) => {
                                const h = bHours(week, pred);
                                return <td key={i} style={{ ...cellStyle, fontSize: '0.6rem', color: '#555' }}>{h ? Math.round(h).toLocaleString() : '—'}</td>;
                              })}
                              {[...Array(13)].map((_, i) => <td key={`f${i}`} style={{ ...cellStyle, background: '#e3f2fd', color: '#999', fontSize: '0.6rem' }}>—</td>)}
                            </tr>
                          </React.Fragment>
                        );
                      });
                    })()}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Monthly View */}
      {!loading && viewMode === 'monthly' && monthlyData.length > 0 && (
        <div className="card" style={{ overflowX: 'auto' }}>
          <table style={{ borderCollapse: 'collapse', fontSize: '0.7rem', width: '100%' }}>
            <thead>
              <tr>
                <th style={{ ...headerStyle, textAlign: 'left', minWidth: 160, position: 'sticky', left: 0, zIndex: 2 }}>Month</th>
                {monthlyData.map((m, i) => <th key={i} style={headerStyle}>{m.monthStart?.slice(0, 7) || `M${i+1}`}</th>)}
              </tr>
            </thead>
            <tbody>
              {PPR_LINES.filter(line => !essentialsOnly || !hiddenLineIds.includes(line.id)).map(line => {
                const rateLabel = line.type === 'uph' ? 'UPH' : 'CPLH';
                return (
                  <React.Fragment key={line.id}>
                    <tr>
                      <td style={{ ...cellStyle, textAlign: 'left', fontWeight: 600, position: 'sticky', left: 0, background: '#fff', zIndex: 1 }}>{line.name} <span style={{ fontSize: '0.55rem', color: '#888' }}>{rateLabel}</span></td>
                      {monthlyData.map((m, i) => {
                        const lineData = m.lines?.[line.id];
                        const rate = lineData?.rate || 0;
                        return <td key={i} style={{ ...cellStyle, background: rate ? '#f5f5f5' : 'transparent' }}>{rate ? rate.toFixed(1) : '—'}</td>;
                      })}
                    </tr>
                    <tr>
                      <td style={{ ...cellStyle, textAlign: 'left', fontSize: '0.6rem', color: '#555', paddingLeft: 16 }}>% to Plan</td>
                      {monthlyData.map((m, i) => {
                        const lineData = m.lines?.[line.id];
                        const pct = lineData?.pctToPlan || 0;
                        return <td key={i} style={{ ...cellStyle, background: getHeatColor(pct), fontSize: '0.6rem' }}>{pct ? pct.toFixed(0) + '%' : '—'}</td>;
                      })}
                    </tr>
                    <tr>
                      <td style={{ ...cellStyle, textAlign: 'left', fontSize: '0.6rem', color: '#555', paddingLeft: 16 }}>Hours</td>
                      {monthlyData.map((m, i) => {
                        const lineData = m.lines?.[line.id];
                        const hours = lineData?.hours || 0;
                        return <td key={i} style={{ ...cellStyle, fontSize: '0.6rem', color: '#555' }}>{hours ? Math.round(hours).toLocaleString() : '—'}</td>;
                      })}
                    </tr>
                    <tr style={{ borderBottom: '2px solid #000' }}>
                      <td style={{ ...cellStyle, textAlign: 'left', fontSize: '0.6rem', paddingLeft: 16 }}>Hrs +/-</td>
                      {monthlyData.map((m, i) => {
                        const lineData = m.lines?.[line.id];
                        const hrsSaved = lineData?.hoursSaved || 0;
                        return <td key={i} style={{ ...cellStyle, fontSize: '0.6rem', background: getHoursColor(hrsSaved) }}>{hrsSaved ? Math.round(hrsSaved).toLocaleString() : '—'}</td>;
                      })}
                    </tr>
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
